import { cva, type VariantProps } from "class-variance-authority";
import {
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import { getWhitelabelCustomization } from "common/helpers/utils";

const inputVariants = cva(
  [
    "relative inline-flex items-center justify-center border border-black-100 bg-black-100 text-black-950 transition-[background,border] [&_svg]:text-black-950 ",
    //placeholder
    "[&_input]:placeholder:text-black-500",
    //hover
    "hover:border-black-200 hover:bg-black-200",
    //disabled
    "has-[:disabled]:cursor-not-allowed has-[:disabled]:text-black-400 has-[:disabled]:hover:bg-black-100 has-[:disabled]:hover:border-black-100",
    //focus
    "has-[:focus-visible]:bg-white has-[:focus-visible]:hover:bg-white",
    //error
    "data-[error=true]:!border-red-400",
    //form field error
    "group-data-[error=true]:!border-red-400",
    //error + focus
    "data-[error=true]:has-[:focus-visible]:outline-red-300 data-[error=true]:has-[:focus-visible]:outline-solid data-[error=true]:has-[:focus-visible]:outline-2",
    //form field error + focus
    "group-data-[error=true]:has-[:focus-visible]:outline-red-300 group-data-[error=true]:has-[:focus-visible]:outline-solid group-data-[error=true]:has-[:focus-visible]:outline-2",
  ],
  {
    variants: {
      variant: {
        sm: "h-8 gap-1 rounded-lg p-2 text-xs-medium [&_svg]:size-4",
        md: "h-10 gap-1.5 rounded-2xl p-2.5 text-sm-medium [&_svg]:size-5",
        lg: "h-11 gap-2 rounded-3xl p-3 text-md-medium [&_svg]:size-5",
      },
      hasWhitelabelColor: {
        true: "has-[:focus-visible]:border-whitelabel-primary has-[:focus-visible]:hover:border-whitelabel-primary",
        false: "has-[:focus-visible]:border-purple-400",
      },
    },
    defaultVariants: {
      variant: "md",
    },
  },
);

interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  leftComponent?: ReactNode;
  rightComponent?: ReactNode;
  hasError?: boolean;
}

function Input(
  {
    className,
    type,
    variant,
    hasError = false,
    leftComponent = null,
    rightComponent = null,
    ...props
  }: InputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  return (
    <div
      className={inputVariants({
        variant,
        className,
        hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
      })}
      data-error={hasError}
    >
      {leftComponent}
      <input
        type={type}
        className="w-full bg-transparent focus-visible:outline-none disabled:cursor-not-allowed"
        ref={ref}
        {...props}
      />
      {rightComponent}
    </div>
  );
}

Input.displayName = "Input";

const InputWithRef = forwardRef(Input);

export { InputWithRef as Input, type InputProps };
